import ChevronRight from '@mui/icons-material/ChevronRight';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { ListItemIcon, MenuItem, Typography } from '@mui/material';
import { KeyboardEvent, ReactElement, ReactNode, forwardRef } from 'react';
import { useTranslate } from 'react-admin';
import { GREEN, GREEN_DARK, GREEN_SOFT, SUBMENU_TRAIL_CLASS } from './styles';

/** Cabe nos 36px da linha da coluna recolhida, com folga de 2px. */
const TILE_SIZE = 32;

export type SubMenuHeaderVariant = 'accordion' | 'rail' | 'row';

interface Props {
    /** O grupo está aberto: acordeão expandido ou painel dele na tela. */
    open: boolean;
    /**
     * A rota atual está dentro deste grupo.
     *
     * É um estado SEPARADO de `open`: o grupo que guarda a página pode estar
     * fechado, e é justamente aí que a marca importa — sem ela, com a barra
     * recolhida, nada diz por onde a navegação entrou.
     */
    containsActive: boolean;
    dense: boolean;
    endAdornment?: ReactNode;
    icon: ReactElement;
    name: string;
    /**
     * Onde este cabeçalho está:
     *  - `accordion`: na coluna aberta, abre para baixo (chevron que gira);
     *  - `rail`: ícone na coluna recolhida, abre o painel ao lado;
     *  - `row`: linha dentro de um painel, abre o painel seguinte em cascata.
     */
    variant: SubMenuHeaderVariant;
    onClick?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onKeyDown?: (event: KeyboardEvent<HTMLLIElement>) => void;
    'aria-expanded'?: boolean;
    'aria-haspopup'?: 'menu';
}

/**
 * A linha clicável do grupo.
 *
 * O ícone FICA quando o grupo abre — antes ele era substituído pelo chevron, e
 * o grupo perdia a identidade justo quando estava em uso.
 */
const SubMenuHeader = forwardRef<HTMLLIElement, Props>((props, ref) => {
    const { open, containsActive, dense, endAdornment, icon, name, variant, ...rest } = props;
    const translate = useTranslate();

    const isRail = variant === 'rail';
    const isRow = variant === 'row';

    return (
        <MenuItem
            ref={ref}
            dense={dense}
            className={isRow && containsActive ? SUBMENU_TRAIL_CLASS : undefined}
            {...rest}
            sx={{
                // Na coluna recolhida não há linha para marcar — quem acende é
                // o ladrilho do ícone, logo abaixo.
                backgroundColor: theme =>
                    open && !isRail ? theme.palette.action.hover : 'transparent',
                ...(isRow
                    ? {}
                    : {
                        borderRadius: 1,
                        mt: 0.25,
                    }),
            }}
        >
            {/*
              * No acordeão a geometria é IDÊNTICA à do MenuItemLink de primeiro
              * nível: mesmo marginLeft/Right e o minWidth padrão do MUI. Mexer
              * aqui desalinha o texto contra os itens que não têm submenu.
              *
              * Na coluna recolhida vira ladrilho, desenhado SEMPRE (só a cor
              * muda com o estado), senão o ícone pularia ao acender. Os -1px
              * compensam o padding de 16px do MenuItem contra os 19px dos
              * vizinhos (o tema põe 3px de borda neles): é o que mantém o glifo
              * no mesmo eixo vertical, medido em 31px.
              *
              * Dentro de um painel o ladrilho já vem da lista (`panelListSx`);
              * aqui só se repinta quando a navegação está dentro do grupo.
              */}
            <ListItemIcon
                sx={
                    isRow
                        ? undefined
                        : {
                            marginLeft: isRail ? '-1px' : 0.5,
                            marginRight: 0.5,
                            ...(isRail && {
                                minWidth: TILE_SIZE,
                                width: TILE_SIZE,
                                height: TILE_SIZE,
                                borderRadius: '10px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: theme => {
                                    if (containsActive) return GREEN_SOFT;
                                    return open ? theme.palette.action.hover : 'transparent';
                                },
                            }),
                            color: theme => {
                                if (containsActive) return GREEN_DARK;
                                if (open) return theme.palette.text.primary;
                                return theme.palette.text.secondary;
                            },
                        }
                }
            >
                {icon}
            </ListItemIcon>

            <Typography
                variant="inherit"
                noWrap
                sx={
                    isRow
                        ? { flexGrow: 1 }
                        : {
                            flexGrow: 1,
                            fontWeight: open || containsActive ? 600 : 400,
                            color: theme => {
                                if (containsActive) return GREEN_DARK;
                                return open
                                    ? theme.palette.text.primary
                                    : theme.palette.text.secondary;
                            },
                        }
                }
            >
                {translate(name)}
            </Typography>

            {endAdornment}

            {variant === 'accordion' && (
                <ExpandMore
                    fontSize="small"
                    sx={{
                        ml: 0.5,
                        color: containsActive ? GREEN : 'text.disabled',
                        transition: 'transform 195ms cubic-bezier(0.4, 0, 0.6, 1)',
                        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                    }}
                />
            )}

            {isRow && (
                <ChevronRight
                    fontSize="small"
                    sx={{
                        ml: 0.5,
                        color: containsActive ? GREEN : 'text.disabled',
                        flex: '0 0 auto',
                    }}
                />
            )}
        </MenuItem>
    );
});

SubMenuHeader.displayName = 'SubMenuHeader';

export default SubMenuHeader;
